import { firstTouchMetadata } from '@/lib/firstTouch';
import { logError } from '@/lib/logError';
import { resetMarketing } from '@/lib/marketing';
import rest from '@/lib/rest';
import { getRewardfulReferral, getRewardfulStatus } from '@/lib/rewardful';
import { getToltReferral, getToltStatus } from '@/lib/tolt';
import { useUserStore } from '@/stores/user';
import type { User } from '@/types/user';
import { RestError } from '@dabble/rest-client';
import { defineStore } from 'pinia';
import posthog from 'posthog-js';
import { ref } from 'vue';

export type SignUpOptions = {
  role?: string;
  /**
   * When true, the REST handler schedules `stripe.setupTrial` (creates the
   * Stripe customer + trial subscription) as part of signup side effects —
   * matching the dabble-writer 2.5 flow. Otherwise no Stripe state is
   * provisioned on signup.
   */
  trial?: boolean;
};

/** SSO identity providers the REST backend can verify (`POST auth/<provider>`). */
export type SsoProvider = 'google';

/**
 * Signup metadata for `POST auth/user` / `POST auth/<provider>`. `trial: true`
 * schedules the backend `stripe.setupTrial` task (Stripe customer + trial created
 * atomically with signup, mirroring dabble-writer 2.5); `role` flows through to
 * customer.io / intercom identify.
 *
 * The affiliate click ids ride along because signup is where the Stripe objects
 * are made: the trial subscription carries the Tolt id, the customer carries the
 * Rewardful one. This is the only place Rewardful is sent, since attribution there
 * has always been new-accounts-only. Tolt also rides the checkout activation, which
 * covers a paid conversion that skipped the trial.
 *
 * `forSignup` is false on the SSO sign-in surface, which shares this builder but
 * creates no account. The server ignores the ids there, so sending them would only
 * put a third-party identifier on the wire for every returning user's login.
 */
async function buildSignupMetadata(
  options?: SignUpOptions,
  forSignup = true,
): Promise<Record<string, string | boolean>> {
  const metadata: Record<string, string | boolean> = {};
  if (options?.trial === true) metadata.trial = true;
  if (options?.role) metadata.role = options.role;
  if (!forSignup) return metadata;

  // The campaign tags the visitor first arrived with (`signup_utm_*`, see
  // lib/firstTouch). dabble-rest sets them once on the new person in the app's
  // PostHog project, which is the only place they can meet the signup.
  Object.assign(metadata, firstTouchMetadata());

  // Both readers wait on their script rather than reading whatever is there yet:
  // an id that resolves a moment late is the difference between an attributed
  // signup and one no affiliate is ever paid for.
  const [toltReferral, rewardfulReferral] = await Promise.all([getToltReferral(), getRewardfulReferral()]);
  if (toltReferral) metadata.tolt_referral = toltReferral;
  if (rewardfulReferral) metadata.rewardful_referral = rewardfulReferral;

  // Attribution failing silently is how this went unnoticed for two years: an
  // unattributed signup and a blocked script look identical from the outside. The
  // load status separates them, so the referral rate is a chart to watch rather
  // than something an affiliate reports.
  posthog.capture('signup_affiliate_attribution', {
    tolt_referral_present: Boolean(toltReferral),
    rewardful_referral_present: Boolean(rewardfulReferral),
    tolt_status: getToltStatus(),
    rewardful_status: getRewardfulStatus(),
  });

  return metadata;
}

export const useAuthStore = defineStore('auth', () => {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const signingIn = ref<boolean>(false);
  const signingUp = ref<boolean>(false);

  async function signUp(name: string, email: string, password: string, options?: SignUpOptions) {
    if (!signingUp.value) {
      signingUp.value = true;
      loading.value = true;
      try {
        // /auth/user reads `req.content.metadata`; see buildSignupMetadata.
        const metadata = await buildSignupMetadata(options);
        const body: {
          name: string;
          email: string;
          password: string;
          metadata?: Record<string, string | boolean>;
        } = { name, email, password };
        if (Object.keys(metadata).length > 0) {
          body.metadata = metadata;
        }

        await rest.post(`auth/user`).send(body);

        const loginResponse = await login(email, password);

        return loginResponse;
      } catch (e: any) {
        error.value = e.message;
        throw e;
      } finally {
        signingUp.value = false;
        loading.value = false;
      }
    }
    return null;
  }

  async function login(email: string, password: string) {
    if (signingIn.value) {
      return null;
    }
    signingIn.value = true;
    /** Avoid clearing `loading` while `signUp` is running — it calls `login()` internally. */
    const manageLoading = !signingUp.value;
    if (manageLoading) {
      loading.value = true;
    }
    error.value = null;
    try {
      const response = await rest.post(`auth/signIn`).credentials('include').send({
        email,
        password,
      });
      if (response?.user?.uid) {
        localStorage.setItem('uid', response.user.uid);
        sessionStorage.setItem('uid', response.user.uid);
        localStorage.setItem('hasLoggedIn', '1');
        sessionStorage.setItem('hasLoggedIn', '1');
      }
      return response;
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      signingIn.value = false;
      if (manageLoading) {
        loading.value = false;
      }
    }
  }

  /**
   * Sign in — or sign up — with an SSO identity token. POSTs the provider's
   * signed credential (a Google ID token JWT from Google Identity Services) to
   * `auth/<provider>`, which verifies it server-side, creates the account when
   * it's new (`isNewUser: true`), and answers with the sealed `dbl2` session
   * cookie — the same session mechanics as `login()`.
   *
   * Errors reject to the caller: 401 `AUTH_FAILED` (invalid/expired ID token),
   * 409 `ACCOUNT_EXISTS` (the provider email collides with an account that
   * needs explicit confirmation to link).
   *
   * Returns `null` when a sign-in is already in flight — the same reentrancy
   * guard as `login()`, so a GIS credential can't race a pending password
   * login into duplicate auth POSTs.
   */
  async function ssoLogin(
    provider: SsoProvider,
    credential: string,
    options?: SignUpOptions,
    mode?: 'signin' | 'signup',
  ): Promise<{ user: User; isNewUser: boolean } | null> {
    if (signingIn.value) {
      return null;
    }
    signingIn.value = true;
    loading.value = true;
    error.value = null;
    try {
      // Same metadata as signUp(); omitted entirely when empty so the wire body
      // stays minimal.
      const metadata = await buildSignupMetadata(options, mode !== 'signin');
      // `mode` lets the backend distinguish the login surface from signup: on
      // 'signin', a Google address with no matching account is rejected
      // (NO_LINKED_ACCOUNT) instead of silently creating a new, empty account.
      const body: { credential: string; mode?: 'signin' | 'signup'; metadata?: Record<string, string | boolean> } = {
        credential,
      };
      if (mode) {
        body.mode = mode;
      }
      if (Object.keys(metadata).length > 0) {
        body.metadata = metadata;
      }

      const response = await rest.post(`auth/${provider}`).credentials('include').send(body);
      if (response?.user?.uid) {
        localStorage.setItem('uid', response.user.uid);
        sessionStorage.setItem('uid', response.user.uid);
        localStorage.setItem('hasLoggedIn', '1');
        sessionStorage.setItem('hasLoggedIn', '1');
      }
      return { user: response.user, isNewUser: response.isNewUser };
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      signingIn.value = false;
      loading.value = false;
    }
  }

  async function logout() {
    const userStore = useUserStore();
    const uid = userStore.user.uid;
    if (uid) {
      try {
        await rest.delete('auth/signIn').header('x-uid', uid).credentials('include').send();
      } catch (err) {
        // The server sign-out is best-effort. If the session is already gone the
        // request answers 401 ("Authorization required") or 403 ("Not permitted
        // to act as another user"); that's an expected condition when logging out
        // — the goal (no live session) is already met. Letting it reject here did
        // two harmful things (DABBLE-ACCOUNTS-2-15): it escaped to the caller's
        // error handler (Sidebar's `preAction`) as a reported error, AND it
        // aborted the local cleanup + redirect below, so logout never actually
        // completed. Swallow the expected auth failures, and report-but-don't-
        // rethrow anything else, so logout always finishes locally.
        if (!(err instanceof RestError && (err.code === 401 || err.code === 403))) {
          logError('Failed to sign out on the server:', err);
        }
      }
    }
    sessionStorage.removeItem('impersonate');
    sessionStorage.removeItem('uid');
    localStorage.removeItem('uid');
    userStore.clear();
    // Reset PostHog identity synchronously before the reload aborts in-flight
    // requests, so the next user (or anonymous session) starts with a fresh
    // distinct_id instead of being merged with this one.
    posthog.reset();
    resetMarketing();
    // The sealed `dbl2` cookie may still hold other signed-in accounts.
    // Reload so accounts.init() re-derives the active uid from the cookie:
    // a remaining account becomes active, or the router falls through to
    // /auth/login when no accounts remain.
    //
    // `/billing/portal`, not `/`: the root hands authenticated users off to the
    // writer app, which would eject you from accounts the moment a remaining
    // account became active. The portal requires auth, so the no-accounts case
    // still bounces to /auth/login — and comes back here afterwards.
    location.assign('/billing/portal');
  }

  return {
    loading,
    signingIn,
    signingUp,
    error,
    login,
    signUp,
    ssoLogin,
    logout,
  };
});
